import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const FHFoodExercisePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fef3f2] via-white to-[#fde8e7]">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-[#f04e58]/10 rounded-full mb-6">
              <span className="text-sm font-semibold text-[#f04e58]">Fact vs Myth</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 leading-tight">
              Familial Hypercholesterolemia (FH):<br />
              <span className="font-semibold text-[#f04e58]">Food &amp; Exercise</span>
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed font-light max-w-3xl mx-auto">
              Understanding the difference between lifestyle-related cholesterol and genetic FH can save lives
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              When someone is told they have high cholesterol, the first advice they often hear is:
            </p>
            <p className="text-2xl font-semibold text-[#f04e58] mb-6 italic">
              "Just eat better and exercise more."
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              For many people, lifestyle changes can meaningfully improve cholesterol levels.
              <br />
              But for individuals with <strong>Familial Hypercholesterolemia (FH)</strong>, the story is very different.
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              FH is a genetic disorder that causes very high LDL ("bad") cholesterol from birth. Diet and exercise are important — but they are not enough on their own.
            </p>
          </div>

          {/* Hero Image */}
          <div className="rounded-2xl overflow-hidden shadow-lg mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/7a684e0d0cbf2e9c167cd5214968c4ff.jpg"
              alt="FH Food and Exercise"
              className="w-full h-[400px] object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* What Is FH */}
      <section className="py-16 bg-gradient-to-br from-[#0d9488]/5 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-light text-gray-900 mb-8">
            What Is <span className="font-semibold text-[#0d9488]">Familial Hypercholesterolemia?</span>
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            FH is an inherited condition that:
          </p>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border-l-4 border-[#0d9488]">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#0d9488]/10 flex-shrink-0">
                  <i className="ri-arrow-up-line text-xl text-[#0d9488]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Raises LDL Significantly</h3>
                  <p className="text-sm text-gray-600">Often ≥190 mg/dL in adults</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border-l-4 border-[#f04e58]">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#f04e58]/10 flex-shrink-0">
                  <i className="ri-close-circle-line text-xl text-[#f04e58]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Impairs LDL Receptor Function</h3>
                  <p className="text-sm text-gray-600">Body cannot effectively clear LDL</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border-l-4 border-[#8b5cf6]">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#8b5cf6]/10 flex-shrink-0">
                  <i className="ri-time-line text-xl text-[#8b5cf6]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Lifelong LDL Exposure</h3>
                  <p className="text-sm text-gray-600">Elevated cholesterol from birth</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border-l-4 border-[#f59e0b]">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#f59e0b]/10 flex-shrink-0">
                  <i className="ri-heart-pulse-line text-xl text-[#f59e0b]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Increases Heart Disease Risk</h3>
                  <p className="text-sm text-gray-600">Dramatically raises premature cardiovascular events</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-[#f04e58]/10 rounded-xl p-6 border border-[#f04e58]/20">
            <p className="text-base text-gray-800 leading-relaxed">
              <strong>It affects approximately 1 in 250 people worldwide</strong>, yet up to 90% remain undiagnosed.
            </p>
          </div>
        </div>
      </section>

      {/* Myth #1 */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#f04e58]/5 to-white rounded-2xl p-8 border border-[#f04e58]/20 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                ❌
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Myth #1: "High cholesterol is caused by bad diet."
                </h3>
              </div>
            </div>
            <div className="pl-16">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0d9488] text-white font-bold flex-shrink-0">
                  ✓
                </div>
                <h4 className="text-xl font-semibold text-[#0d9488] mt-0.5">Fact:</h4>
              </div>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                In FH, high LDL is caused by a <strong>genetic mutation</strong>, not lifestyle.
              </p>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Most cases of FH involve mutations in the LDL receptor pathway, meaning the body cannot effectively clear LDL from the bloodstream.
              </p>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Even with:
              </p>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-lg mt-0.5"></i>
                  <span className="text-gray-700">Perfect diet</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-lg mt-0.5"></i>
                  <span className="text-gray-700">Regular exercise</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-lg mt-0.5"></i>
                  <span className="text-gray-700">Ideal body weight</span>
                </li>
              </ul>
              <p className="text-lg font-semibold text-gray-900 mb-4">
                LDL levels in FH remain abnormally high.
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                Lifestyle contributes to overall heart health — but it does not "cure" FH.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Myth #2 */}
      <section className="py-16 bg-gradient-to-br from-[#3b82f6]/5 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-2xl p-8 border border-[#3b82f6]/20 shadow-sm mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                ❌
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Myth #2: "If I exercise daily, I can reverse FH."
                </h3>
              </div>
            </div>
            <div className="pl-16">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0d9488] text-white font-bold flex-shrink-0">
                  ✓
                </div>
                <h4 className="text-xl font-semibold text-[#0d9488] mt-0.5">Fact:</h4>
              </div>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Exercise improves cardiovascular health — but it does not normalize genetically elevated LDL.
              </p>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Research shows:
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-[#3b82f6]/5 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-heart-add-line text-[#3b82f6] text-xl mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Raises HDL</p>
                      <p className="text-xs text-gray-600">("good" cholesterol)</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#3b82f6]/5 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-pulse-line text-[#3b82f6] text-xl mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Improves Insulin Sensitivity</p>
                      <p className="text-xs text-gray-600">Better glucose control</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#3b82f6]/5 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-shield-check-line text-[#3b82f6] text-xl mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Reduces Inflammation</p>
                      <p className="text-xs text-gray-600">Lower systemic inflammation</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#3b82f6]/5 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-heart-line text-[#3b82f6] text-xl mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Enhances Endothelial Function</p>
                      <p className="text-xs text-gray-600">Better blood vessel health</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-[#f59e0b]/10 rounded-lg p-4 mb-4">
                <p className="text-base text-gray-800 leading-relaxed">
                  However, <strong>LDL reduction from exercise alone is modest — typically 5–10%</strong>.
                </p>
              </div>
              <p className="text-base text-gray-700 leading-relaxed mb-2">
                In FH, LDL may be 2–4 times normal levels.
              </p>
              <p className="text-lg font-semibold text-gray-900">
                That gap requires medication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Myth #3 */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#ec4899]/5 to-white rounded-2xl p-8 border border-[#ec4899]/20 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                ❌
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Myth #3: "Children don't need to worry about cholesterol."
                </h3>
              </div>
            </div>
            <div className="pl-16">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0d9488] text-white font-bold flex-shrink-0">
                  ✓
                </div>
                <h4 className="text-xl font-semibold text-[#0d9488] mt-0.5">Fact:</h4>
              </div>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                In FH, cholesterol exposure begins at birth.
              </p>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Studies show:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-[#ec4899] text-lg mt-0.5"></i>
                  <span className="text-gray-700">Arterial fatty streaks can develop in childhood</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-[#ec4899] text-lg mt-0.5"></i>
                  <span className="text-gray-700">Carotid artery thickening appears in adolescence</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-error-warning-line text-[#ec4899] text-lg mt-0.5"></i>
                  <span className="text-gray-700">Untreated individuals may experience heart attacks in their 30s or 40s</span>
                </li>
              </ul>
              <div className="bg-[#f04e58]/10 rounded-lg p-4 mb-4">
                <p className="text-base text-gray-800 leading-relaxed">
                  In severe cases like <strong>Homozygous Familial Hypercholesterolemia (HoFH)</strong>, heart disease can appear in childhood.
                </p>
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                Early screening between ages 9–11 — or earlier with family history — is recommended.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Myth #4 */}
      <section className="py-16 bg-gradient-to-br from-[#8b5cf6]/5 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-2xl p-8 border border-[#8b5cf6]/20 shadow-sm mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f04e58] text-white font-bold text-xl flex-shrink-0">
                ❌
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Myth #4: "If I feel fine, I don't need treatment."
                </h3>
              </div>
            </div>
            <div className="pl-16">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0d9488] text-white font-bold flex-shrink-0">
                  ✓
                </div>
                <h4 className="text-xl font-semibold text-[#0d9488] mt-0.5">Fact:</h4>
              </div>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                FH is often completely silent until a major cardiac event.
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h5 className="text-base font-semibold text-gray-900 mb-3">There may be:</h5>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-line text-gray-400 text-lg mt-0.5"></i>
                      <span className="text-gray-700">No symptoms</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-line text-gray-400 text-lg mt-0.5"></i>
                      <span className="text-gray-700">No warning signs</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-line text-gray-400 text-lg mt-0.5"></i>
                      <span className="text-gray-700">No pain</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h5 className="text-base font-semibold text-gray-900 mb-3">Until suddenly:</h5>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-3">
                      <i className="ri-alert-line text-[#f04e58] text-lg mt-0.5"></i>
                      <span className="text-gray-700">A heart attack</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-alert-line text-[#f04e58] text-lg mt-0.5"></i>
                      <span className="text-gray-700">Stroke</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-alert-line text-[#f04e58] text-lg mt-0.5"></i>
                      <span className="text-gray-700">Need for bypass surgery</span>
                    </li>
                  </ul>
                </div>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                This is why early diagnosis and preventive treatment are critical.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Role of Diet */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-light text-gray-900 mb-8">
            The Role of <span className="font-semibold text-[#0d9488]">Diet</span> in FH
          </h2>
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-8">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              While diet alone cannot normalize LDL in FH, it still plays an important role.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">A heart-healthy diet can:</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="flex items-start gap-3">
                <i className="ri-arrow-down-line text-[#0d9488] text-xl mt-0.5"></i>
                <span className="text-gray-700">Reduce LDL modestly</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-arrow-down-line text-[#0d9488] text-xl mt-0.5"></i>
                <span className="text-gray-700">Lower triglycerides</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-arrow-down-line text-[#0d9488] text-xl mt-0.5"></i>
                <span className="text-gray-700">Improve blood pressure</span>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-arrow-down-line text-[#0d9488] text-xl mt-0.5"></i>
                <span className="text-gray-700">Reduce inflammation</span>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Recommended dietary patterns include:</h3>
            <div className="space-y-3 mb-6">
              <div className="bg-[#0d9488]/5 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <i className="ri-restaurant-line text-[#0d9488] text-xl"></i>
                  <span className="text-gray-800 font-medium">Mediterranean-style eating</span>
                </div>
              </div>
              <div className="bg-[#0d9488]/5 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <i className="ri-plant-line text-[#0d9488] text-xl"></i>
                  <span className="text-gray-800 font-medium">High fiber intake</span>
                </div>
              </div>
              <div className="bg-[#0d9488]/5 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <i className="ri-arrow-down-circle-line text-[#0d9488] text-xl"></i>
                  <span className="text-gray-800 font-medium">Reduced saturated fat</span>
                </div>
              </div>
              <div className="bg-[#0d9488]/5 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <i className="ri-close-circle-line text-[#0d9488] text-xl"></i>
                  <span className="text-gray-800 font-medium">Minimal trans fats</span>
                </div>
              </div>
              <div className="bg-[#0d9488]/5 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <i className="ri-leaf-line text-[#0d9488] text-xl"></i>
                  <span className="text-gray-800 font-medium">Increased plant-based foods</span>
                </div>
              </div>
            </div>
            <div className="bg-[#f59e0b]/10 rounded-lg p-4">
              <p className="text-base text-gray-800 leading-relaxed font-semibold">
                Diet supports treatment — but it does not replace medication in FH.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Role of Exercise */}
      <section className="py-16 bg-gradient-to-br from-[#3b82f6]/5 to-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-light text-gray-900 mb-8">
            The Role of <span className="font-semibold text-[#3b82f6]">Exercise</span> in FH
          </h2>
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-8">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Regular physical activity:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-[#3b82f6]/5 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-heart-pulse-line text-[#3b82f6] text-xl mt-0.5"></i>
                  <span className="text-gray-700">Improves vascular function</span>
                </div>
              </div>
              <div className="bg-[#3b82f6]/5 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-arrow-down-line text-[#3b82f6] text-xl mt-0.5"></i>
                  <span className="text-gray-700">Lowers blood pressure</span>
                </div>
              </div>
              <div className="bg-[#3b82f6]/5 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-pulse-line text-[#3b82f6] text-xl mt-0.5"></i>
                  <span className="text-gray-700">Reduces insulin resistance</span>
                </div>
              </div>
              <div className="bg-[#3b82f6]/5 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-mental-health-line text-[#3b82f6] text-xl mt-0.5"></i>
                  <span className="text-gray-700">Improves mental health</span>
                </div>
              </div>
              <div className="bg-[#3b82f6]/5 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-scales-line text-[#3b82f6] text-xl mt-0.5"></i>
                  <span className="text-gray-700">Helps control weight</span>
                </div>
              </div>
            </div>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              Exercise reduces overall cardiovascular risk, even if LDL remains elevated.
            </p>
            <div className="bg-[#f59e0b]/10 rounded-lg p-4">
              <p className="text-base text-gray-800 leading-relaxed font-semibold">
                In FH, exercise is part of prevention — not a substitute for LDL-lowering therapy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Happens If FH Is Not Treated */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What Happens If FH Is Not Treated?
          </h2>
          <div className="bg-gradient-to-br from-[#fef9f9] to-white rounded-2xl p-8 border border-gray-100">
            <p className="text-gray-700 leading-relaxed mb-4">
              Untreated FH significantly increases the risk of:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Premature coronary artery disease</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Myocardial infarction (heart attack)</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Stroke</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Multi-vessel coronary disease</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Sudden cardiac death</span>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              Men with untreated FH historically faced high risk of heart attack before age 50.
              Women often experience events after menopause — but still earlier than the general population.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The cumulative exposure to high LDL — known as <strong>"cholesterol-year burden"</strong> — drives this risk.
            </p>
          </div>
        </div>
      </section>

      {/* How to Diagnose FH Early */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            How to Diagnose FH Early
          </h2>
          <div className="bg-white rounded-2xl p-8 mb-8 border border-gray-100">
            <p className="text-gray-700 leading-relaxed mb-6 text-lg font-semibold">
              Early diagnosis saves lives.
            </p>
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Evaluation is recommended if:
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">LDL ≥190 mg/dL in adults</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">LDL ≥160 mg/dL in children with family history</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Family history of early heart disease</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Known FH mutation in family</span>
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Diagnosis may involve:
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#fef9f9] rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <i className="ri-test-tube-line text-[#0d9488] text-xl"></i>
                    <h4 className="font-semibold text-gray-900">Lipid testing</h4>
                  </div>
                </div>
                <div className="bg-[#fef9f9] rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <i className="ri-file-list-3-line text-[#0d9488] text-xl"></i>
                    <h4 className="font-semibold text-gray-900">Clinical criteria</h4>
                  </div>
                  <p className="text-sm text-gray-600">(Dutch Lipid Clinic Network, Simon Broome)</p>
                </div>
                <div className="bg-[#fef9f9] rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <i className="ri-dna-line text-[#0d9488] text-xl"></i>
                    <h4 className="font-semibold text-gray-900">Genetic testing</h4>
                  </div>
                </div>
                <div className="bg-[#fef9f9] rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <i className="ri-group-line text-[#0d9488] text-xl"></i>
                    <h4 className="font-semibold text-gray-900">Cascade screening</h4>
                  </div>
                  <p className="text-sm text-gray-600">of relatives</p>
                </div>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed">
              Because FH is inherited in an autosomal dominant pattern, each child of an affected parent has a <strong>50% chance</strong> of inheriting it.
            </p>
          </div>
        </div>
      </section>

      {/* How to Prevent Complications */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            How to Prevent Complications
          </h2>
          <div className="bg-gradient-to-r from-[#0d9488] to-[#14b8a6] rounded-2xl p-8 text-white mb-8">
            <p className="text-lg text-white/95 leading-relaxed mb-6">
              Prevention in FH focuses on aggressive LDL reduction.
            </p>
            <h3 className="text-xl font-semibold mb-4">Evidence-based treatments include:</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <i className="ri-medicine-bottle-line text-2xl"></i>
                  <span className="font-medium">High-intensity statins</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <i className="ri-medicine-bottle-line text-2xl"></i>
                  <span className="font-medium">Ezetimibe</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <i className="ri-medicine-bottle-line text-2xl"></i>
                  <span className="font-medium">PCSK9 inhibitors</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <i className="ri-medicine-bottle-line text-2xl"></i>
                  <span className="font-medium">Inclisiran</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm md:col-span-2">
                <div className="flex items-center gap-3">
                  <i className="ri-medicine-bottle-line text-2xl"></i>
                  <span className="font-medium">LDL apheresis (in severe cases)</span>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
              <h3 className="text-lg font-semibold mb-4">When LDL is lowered early and aggressively:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                  <span>Heart attack risk drops by up to <strong>80%</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                  <span>Life expectancy approaches normal</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                  <span>Plaque progression slows significantly</span>
                </li>
              </ul>
            </div>

            <p className="text-lg font-semibold mt-6 text-center">
              The earlier treatment begins, the greater the benefit.
            </p>
          </div>
        </div>
      </section>

      {/* The Balanced Message */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            The Balanced Message
          </h2>
          <div className="bg-white rounded-2xl p-8 border border-gray-100 mb-8">
            <p className="text-xl font-semibold text-gray-900 mb-6">
              Diet and exercise absolutely matter.
            </p>
            <div className="bg-[#fef9f9] rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">They:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Strengthen the cardiovascular system</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Reduce inflammation</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-gray-700">Improve metabolic health</span>
                </li>
              </ul>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              But in Familial Hypercholesterolemia, they are <strong>supportive tools, not standalone solutions</strong>.
            </p>
            <div className="bg-gradient-to-r from-[#f04e58]/10 to-[#e67e22]/10 rounded-xl p-6 border-l-4 border-[#f04e58]">
              <p className="text-gray-900 leading-relaxed font-semibold">
                The real danger is assuming that lifestyle alone can manage a genetic condition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final Takeaway */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-6 text-center">
            Final Takeaway
          </h2>
          <div className="text-center mb-8">
            <p className="text-white/90 text-lg leading-relaxed mb-4">
              Familial Hypercholesterolemia is not caused by poor lifestyle choices.
            </p>
            <p className="text-white text-xl font-semibold mb-8">
              It is a genetic condition that requires medical treatment in addition to healthy habits.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <p className="text-white/90 leading-relaxed mb-4">
              If you or a family member has:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#14b8a6] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-white/90">LDL above 190 mg/dL</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#14b8a6] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-white/90">Early heart disease in the family</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#14b8a6] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-white/90">High cholesterol despite healthy living</span>
              </li>
            </ul>
            <p className="text-white font-semibold text-lg">
              Ask about evaluation for FH.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-[#0d9488] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-restaurant-line text-3xl text-white"></i>
              </div>
              <h3 className="text-white font-semibold mb-2">Food & Exercise</h3>
              <p className="text-white/80 text-sm">Protect the heart</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-[#f04e58] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
              </div>
              <h3 className="text-white font-semibold mb-2">Diagnosis & Therapy</h3>
              <p className="text-white/80 text-sm">Protect life</p>
            </div>
          </div>

          <p className="text-white text-lg leading-relaxed text-center font-semibold">
            Because in FH, awareness plus early treatment changes everything.
          </p>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
            Have Questions About <span className="font-semibold">FH Management?</span>
          </h2>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            Schedule a consultation to discuss your cholesterol management strategy
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consultation"
              className="px-8 py-4 bg-[#f04e58] text-white text-base font-semibold rounded-full hover:bg-[#d9434c] transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Schedule Consultation
            </Link>
            <Link
              to="/topics"
              className="px-8 py-4 bg-white text-gray-900 text-base font-semibold rounded-full hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
            >
              Explore More Topics
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FHFoodExercisePage;
